import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Trophy, MapPin, Radio, Circle, Clock } from "lucide-react";
import { fileUrl } from "@/components/ImageUpload";
import LiveBracket from "@/components/tournament/LiveBracket";
import SponsorTicker from "@/components/SponsorTicker";
import AdShowcase from "@/components/AdShowcase";
import MatchClock from "@/components/MatchClock";
import api from "@/lib/api";

function ServeDots({ n }) {
  const count = n === 2 ? 2 : 1;
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={`serve-dots-${count}`} title={`${count === 2 ? "2nd" : "1st"} serve`}>
      {Array.from({ length: count }).map((_, i) => <Circle key={i} className="w-2 h-2 fill-current text-primary-c" />)}
    </span>
  );
}

function LiveMatchCard({ m }) {
  return (
    <div className="rounded-2xl glow-border p-4" style={{ background: "var(--surface)" }} data-testid={`spectator-live-${m.match_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
        <MatchClock startedAt={m.started_at} className="text-[10px] text-primary-c" iconClass="w-3 h-3" />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold flex items-center gap-1.5">{m.server === "a" && <ServeDots n={m.server_number} />}{m.a_name}</span>
        <span className="font-display font-black text-2xl mono">{m.a}</span>
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-bold flex items-center gap-1.5">{m.server === "b" && <ServeDots n={m.server_number} />}{m.b_name}</span>
        <span className="font-display font-black text-2xl mono">{m.b}</span>
      </div>
    </div>
  );
}

export default function Spectator() {
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");

  const load = useCallback(() => {
    api.get(`/public/tournaments/${tid}/live`).then(({ data }) => {
      setData(data);
      setCat((c) => c || data.categories[0]?.id || "");
    }).catch(() => setData(false));
  }, [tid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "var(--bg)", color: "var(--text)" }}><div><Trophy className="w-10 h-10 mx-auto text-primary-c opacity-60" /><p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>This tournament isn't live yet.</p></div></div>;

  const t = data.tournament;
  const activeCat = data.categories.find((c) => c.id === cat);

  return (
    <div className="min-h-screen flex flex-col cyber-grid" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="relative">
        <div className="aspect-[5/1] min-h-[100px] max-h-[180px] overflow-hidden" style={{ background: "var(--surface)" }}>
          {t.banner_url ? <img src={fileUrl(t.banner_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Trophy className="w-8 h-8 text-primary-c opacity-30" /></div>}
        </div>
        <div className="px-4 py-4">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /><span className="text-[10px] mono uppercase tracking-wider text-primary-c">Live spectator</span></div>
          <h1 className="font-display font-bold text-2xl mt-1 break-words" data-testid="spectator-name">{t.name}</h1>
          {t.venue && <div className="text-sm flex items-center gap-1.5 mt-1" style={{ color: "var(--text-2)" }}><MapPin className="w-4 h-4 text-primary-c" />{t.venue}</div>}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-6 px-4 pb-6 grain">
        <div className="flex-1 min-w-0">
        {data.live_matches.length > 0 && (
          <div className="mb-6" data-testid="spectator-live-matches">
            <h2 className="font-display font-bold flex items-center gap-2 mb-3"><Radio className="w-4 h-4 text-primary-c" /> Now playing</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{data.live_matches.map((m) => <LiveMatchCard key={m.match_id} m={m} />)}</div>
          </div>
        )}

        {(data.upcoming_matches || []).length > 0 && (
          <div className="mb-6" data-testid="spectator-upcoming">
            <h2 className="font-display font-bold flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-primary-c" /> Coming up</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {data.upcoming_matches.slice(0, 6).map((m) => (
                <div key={m.match_id} className="rounded-2xl glow-border px-4 py-2.5" style={{ background: "var(--surface)" }} data-testid={`spectator-upcoming-${m.match_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
                    {m.court_name && <div className="text-[10px] mono uppercase text-primary-c">{m.court_name}</div>}
                  </div>
                  <div className="text-sm font-bold mt-0.5 truncate">{m.a_name} <span className="text-primary-c">vs</span> {m.b_name}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(data.recent_results || []).length > 0 && (
          <div className="mb-6" data-testid="spectator-results">
            <h2 className="font-display font-bold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4" style={{ color: "#eab308" }} /> Recent results</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {data.recent_results.slice(0, 8).map((m) => (
                <div key={m.match_id} className="rounded-2xl glow-border px-4 py-2.5" style={{ background: "var(--surface)" }} data-testid={`spectator-result-${m.match_id}`}>
                  <div className="text-[10px] mono uppercase tracking-wider mb-1" style={{ color: "var(--text-2)" }}>{m.category_label}{m.round_label ? ` · ${m.round_label}` : ""}</div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm truncate ${m.winner_name === m.a_name ? "font-bold" : ""}`} style={{ color: m.winner_name === m.a_name ? "#22c55e" : "var(--text)" }}>{m.a_name}</span>
                    <span className="mono font-black">{m.a}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm truncate ${m.winner_name === m.b_name ? "font-bold" : ""}`} style={{ color: m.winner_name === m.b_name ? "#22c55e" : "var(--text)" }}>{m.b_name}</span>
                    <span className="mono font-black">{m.b}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-1.5 flex-wrap mb-4" data-testid="spectator-cat-tabs">
          {data.categories.map((c) => (
            <button key={c.id} data-testid={`spectator-cat-${c.id}`} onClick={() => setCat(c.id)}
              className="rounded-full px-3 py-1.5 text-xs font-bold transition-colors"
              style={cat === c.id ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--surface)", color: "var(--text-2)", border: "1px solid var(--border)" }}>{c.label}</button>
          ))}
        </div>
        <motion.div key={cat} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          {activeCat ? <LiveBracket category={activeCat} /> : <p className="text-sm" style={{ color: "var(--text-2)" }}>No categories.</p>}
        </motion.div>
        </div>
        <AdShowcase />
      </div>

      <SponsorTicker />
    </div>
  );
}
